import {immerable} from 'immer';
import mapValues from 'lodash/mapValues';

import {MinimalProjectMetadata, ProjectMetadata, ProjectMetadataMap} from 'app_api/projects/types';
import {
  FeatureGate,
  FeatureGatesValue,
  INTERNAL_PROJECTS,
  Permission,
  ProjectFeatureFlag,
  ProjectType,
  RestrictedReport,
  RestrictEntitySaving,
} from 'common/report/constants';
import {extendWorkspace, toWorkspaceObj, WorkspaceObj} from 'common/report/queries/app-api/workspaces-client';
import {purgeFetchCache} from 'common/report/queries/utils/fetch-cache';
import {apiClient} from 'common/util/api-client';
import {ApiResourceClient} from 'common/util/model-resource/api-resource';
import {getFeatureFlagUrlOverrides} from 'common/util/url';
import {TIMEZONES} from 'common/constants';

import {appApiUrl} from '../utils';

// "minimal" project model used in collections (as opposed to richer "CurrentProject")
export class Project {
  [immerable] = true;

  public domain: string;
  public hasWorkspaces: boolean;
  public id: number;
  public isDemo: boolean;
  public name: string;
  public organizationId: number;
  public timePeriodSettings: {
    weekStartDay: number;
    quarterStartMonth: number;
  };
  public type: ProjectType;
  public workspaces: {[key: string]: WorkspaceObj};

  constructor(projectAttrs: MinimalProjectMetadata) {
    this.domain = projectAttrs.domain;
    this.hasWorkspaces = projectAttrs.has_workspaces;
    this.id = projectAttrs.id;
    this.isDemo = projectAttrs.is_demo;
    this.name = projectAttrs.name;
    this.organizationId = projectAttrs.organization_id;
    this.type = projectAttrs.type;

    this.timePeriodSettings = {
      weekStartDay: projectAttrs.time_period_settings?.week_start_day ?? 0,
      quarterStartMonth: projectAttrs.time_period_settings?.quarter_start_month ?? 0,
    };
    this.workspaces = mapValues(projectAttrs.workspaces, (minimalWorkspace) => extendWorkspace({}, minimalWorkspace));
  }

  public isRollupProject(): boolean {
    return this.type === ProjectType.Rollup;
  }
}

export interface ExternalHelpLink {
  linkName: string;
  linkURL: string;
}

interface Role {
  id?: number;
  name?: string;
}

interface Blocking {
  isBlockedEvents: boolean;
  isBlockedPeople: boolean;
  label: string;
}

export interface ProjectTimezone {
  timezone: string;
  utcOffset: number;
}

export interface CreditInfo {
  appliesTo?: string;
  creditTypeId?: number;
  endDate?: string;
  initialValue?: string;
  name?: string;
  remainingBalance?: string;
  state?: string;
}

export interface CreditBalanceInfo {
  creditUsageAmount: number;
  initialValue: number;
  remainingBalance: number;
}

export enum CreditState {
  Valid = `valid`,
  Redeemed = `redeemed`,
  Expired = `expired`,
  NotEffective = `not_effective`,
}

// Note: if needing to change these values for whatever reason, make sure the enum values match with CrossProjectSetting located in project/models.py
export enum BoardMoveSetting {
  NOT_ALLOWED = `Disallowed`,
  ALLOWED_WITHIN_ORG = `Allowed within org`,
  ALLOWED_ALL_ORGS = `Allowed all orgs`,
}

// For reference,
// SELECT DISTINCT name, id from invoicing_billingcredittype;
export const STARTUP_CREDIT_TYPE_ID = 15;

export class CurrentProject extends Project {
  [immerable] = true;

  public blocking: Blocking;
  public creditInfos: CreditInfo[];
  public crossProjectBoardMovingSetting: BoardMoveSetting;
  public eventsPlanID: number;
  public eventsPlanName: string;
  public externalHelpLinks: ExternalHelpLink[];
  public hasIntegratedArb: boolean;
  public hasIntegratedEngage: boolean;
  public hasPublicDashboardsEnabled: boolean;
  public hasSensitiveAccess: boolean;
  public onboardingDashboardID: number;
  public ownerID: number;
  public peoplePlanID: number;
  public projectAsOfDate: null | string;
  public role: Role;
  public timezone: typeof TIMEZONES[number];
  public utcOffset: number;

  /** Reflects the Data Governance setting */
  public hasEventApprovalEnabled: boolean;

  // these should be accessed through helpers hasFeatureFlag() etc
  private featureFlags: Set<string>;
  private permissions: Set<string>;
  private featureGates: FeatureGatesValue;
  private restrictedReports: Set<string>;
  private restrictedEntitySaving: Set<string>;

  constructor(projectAttrs: ProjectMetadata) {
    super(projectAttrs);

    this.blocking = {
      isBlockedEvents: projectAttrs.blocking.is_blocked_events,
      isBlockedPeople: projectAttrs.blocking.is_blocked_people,
      label: projectAttrs.blocking.label,
    };
    this.creditInfos =
      projectAttrs.credit_infos?.map((creditInfo) => {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const {applies_to, credit_type_id, end_date, initial_value, name, remaining_balance, state} = creditInfo;
        return {
          appliesTo: applies_to,
          creditTypeId: credit_type_id,
          endDate: end_date,
          initialValue: initial_value,
          name,
          remainingBalance: remaining_balance,
          state,
        };
      }) || [];
    this.crossProjectBoardMovingSetting = projectAttrs.cross_project_board_move_setting as BoardMoveSetting;
    this.eventsPlanID = projectAttrs.events_plan_id;
    this.eventsPlanName = projectAttrs.events_plan_name;
    this.externalHelpLinks = projectAttrs.external_help_link.map((link) => ({
      linkName: link.link_name,
      linkURL: link.link_url,
    }));
    this.featureFlags = new Set(projectAttrs.feature_flags);
    this.featureGates = projectAttrs.feature_gates as FeatureGatesValue;
    this.hasIntegratedArb = projectAttrs.has_integrated_arb;
    this.hasIntegratedEngage = projectAttrs.has_integrated_engage;
    this.hasPublicDashboardsEnabled = projectAttrs.has_public_dashboards_enabled;
    this.hasEventApprovalEnabled = projectAttrs.has_event_approval_enabled;
    this.hasSensitiveAccess = projectAttrs.hasSensitiveAccess;
    this.onboardingDashboardID = projectAttrs.onboarding_dashboard_id;
    this.ownerID = projectAttrs.owner_id;
    this.peoplePlanID = projectAttrs.people_plan_id;
    this.permissions = new Set(projectAttrs.permissions);
    this.projectAsOfDate = projectAttrs.project_as_of_date;
    this.restrictedReports = new Set(this.featureGates?.[FeatureGate.RestrictedReports]);
    this.restrictedEntitySaving = new Set(this.featureGates?.[FeatureGate.RestrictEntitySaving]);
    this.role = projectAttrs.role || {};
    this.timezone = projectAttrs.timezone as typeof TIMEZONES[number];
    this.utcOffset = projectAttrs.utc_offset;
    this.workspaces = mapValues(projectAttrs.workspaces, toWorkspaceObj);
  }

  public isInternal() {
    return INTERNAL_PROJECTS.has(this.id);
  }

  public hasFeatureFlag(flagName: ProjectFeatureFlag, enableInDogfoodProjects = false) {
    // TODO support injecting a URLManager
    const featureFlagOverrides = getFeatureFlagUrlOverrides();
    if (Object.prototype.hasOwnProperty.call(featureFlagOverrides, flagName)) {
      return featureFlagOverrides[flagName];
    }
    return (
      this.featureFlags.has(flagName) || (enableInDogfoodProjects && this.isInternal()) || this.featureFlags.has(`dev`)
    );
  }

  public getFeatureGate<K extends keyof FeatureGatesValue>(gate: K): FeatureGatesValue[K] | null {
    return this.featureGates?.[gate];
  }

  public hasPermission(permissionName: string) {
    return this.permissions.has(permissionName);
  }

  public getStartupCreditInfos(): CreditInfo[] {
    return (
      this.creditInfos?.filter(
        ({creditTypeId, remainingBalance, state}) =>
          creditTypeId === STARTUP_CREDIT_TYPE_ID && state === CreditState.Valid && parseFloat(remainingBalance) > 0,
      ) || []
    );
  }

  public hasStartupCreditAndFreeUser(): boolean {
    return this.isFreePlanBillingAdmin() && this.getStartupCreditInfos().length > 0;
  }

  public isFreePlanBillingAdmin(): boolean {
    return this.hasPermission(Permission.UpdateOrgBilling) && this.eventsPlanName === `Free`;
  }

  public getStartupCreditBalances(): Nullable<CreditBalanceInfo> {
    const initialValue = this.getStartupCreditInfos()[0]?.initialValue;
    const remainingBalance = this.getStartupCreditInfos()[0]?.remainingBalance;
    const parsedInitialValue = parseFloat(initialValue) || 0;
    const parsedRemainingBalance = parseFloat(remainingBalance) || 0;
    return this.hasStartupCreditAndFreeUser()
      ? {
          creditUsageAmount: parsedInitialValue - parsedRemainingBalance,
          initialValue: parsedInitialValue,
          remainingBalance: parsedRemainingBalance,
        }
      : null;
  }
  public hasAccessToReport(name: RestrictedReport) {
    return !this.restrictedReports.has(name);
  }

  public canSaveEntity(name: RestrictEntitySaving) {
    return !this.restrictedEntitySaving.has(name);
  }

  public isPeopleOnlyProject() {
    return (
      (this.blocking.isBlockedEvents || !this.hasIntegratedArb) &&
      this.hasIntegratedEngage &&
      !this.blocking.isBlockedPeople
    );
  }

  public canUseCohorts(): boolean {
    return this.hasAccessToReport(RestrictedReport.Cohorts) || this.hasFeatureFlag(ProjectFeatureFlag.Cohorts);
  }

  public canUseDataGroups(): boolean {
    return this.hasFeatureFlag(ProjectFeatureFlag.B2BAnalytics) || !!this.getFeatureGate(FeatureGate.B2BAnalytics);
  }
}

const INDEX_PATH = appApiUrl(`projects/metadata/index`);
export const allProjectsClient: ApiResourceClient<Project> = {
  index() {
    const fetches = apiClient.hydratedGet<ProjectMetadataMap>(INDEX_PATH);
    return mapValues(fetches, (fetch) =>
      fetch.then((projects) => projects && mapValues(projects, (projectAttrs) => new Project(projectAttrs))),
    );
  },

  purgeCache() {
    purgeFetchCache({everything: true, path: INDEX_PATH});
  },
};

function projectPath(projectId) {
  return appApiUrl(`metadata`, {projectId});
}
export const currentProjectClient: ApiResourceClient<CurrentProject> = {
  index() {
    throw new Error(`currentProjectClient does not support index queries`);
  },

  get(id) {
    if (!id) {
      // GOOT for public dashboard redirecting after //metadata
      throw new Error(`project id not found`);
    }
    const fetches = apiClient.hydratedGet<ProjectMetadata>(projectPath(id));
    return mapValues(fetches, (fetch) =>
      fetch.then((projectAttrs) => projectAttrs && new CurrentProject(projectAttrs)),
    );
  },

  purgeCache(id) {
    purgeFetchCache({everything: true, path: projectPath(id)});
  },
};
